import {
  needsCouponDiscount,
  needsOfferCopyLine,
  needsTextDiscount,
  needsTierRules,
} from '@/components/ui/OfferProductPreview/brandOfferTilePreview.rules';
import type {
  BrandOffer,
  BrandOfferPayload,
  OfferType,
} from '@/types/BrandOffer.types';

/** Sale types trigger server-side validation that `endsAt` is present on PATCH. */
function backendRequiresEndsAtForOfferType(offerType: OfferType): boolean {
  return offerType === 'SALE' || offerType === 'SALE_EXTRA_OFFERS';
}

function backendRequiresDiscountConfigForOfferType(
  offerType: OfferType
): boolean {
  return (
    needsCouponDiscount(offerType) ||
    needsTextDiscount(offerType) ||
    needsTierRules(offerType) ||
    needsOfferCopyLine(offerType)
  );
}

/**
 * Ensures partial update payloads include `endsAt` when the API requires it
 * (toggle active, bulk actions, etc.).
 */
export function mergeEndsAtForSaleOfferPatch(
  offer: Pick<BrandOffer, 'offerType' | 'endsAt'>,
  payload: Partial<BrandOfferPayload>
): Partial<BrandOfferPayload> {
  if (!backendRequiresEndsAtForOfferType(offer.offerType)) return payload;
  if (payload.endsAt !== undefined) return payload;
  return {
    ...payload,
    endsAt: offer.endsAt ? new Date(offer.endsAt).toISOString() : null,
  };
}

/**
 * List/detail toggles send a minimal PATCH; the API still validates
 * `discount_config` for coupon and tiered types. Merge it from the current row.
 */
export function mergePartialOfferPatch(
  offer: BrandOffer,
  payload: Partial<BrandOfferPayload>
): Partial<BrandOfferPayload> {
  let merged = payload;
  if (
    backendRequiresDiscountConfigForOfferType(offer.offerType) &&
    payload.discountConfig === undefined
  ) {
    merged = { ...payload, discountConfig: offer.discountConfig };
  }
  return mergeEndsAtForSaleOfferPatch(offer, merged);
}
